import { create } from 'zustand';
import { syncService } from '../services/syncService';
import type { SyncStatusResponse } from '../types/sync/syncStatusResponse';

interface SyncState {
    syncing: boolean;
    progress: SyncStatusResponse | null;
    syncStatus: string | null;
    // Derived, persistent counterpart to `syncing`: set once a run ends without reaching a Completed
    // progress despite having written a syncStatus message, so a failure survives `syncing` resetting
    // to false. Cleared at the start of the next attempt and on `resetSync`.
    syncFailed: boolean;
    initialSyncRequired: boolean;
    // Timestamp of the last successful sync, surfaced to the user as the freshness label on Home.
    // Held here rather than fetched per consumer so one status call serves every reader.
    lastSyncedAt: Date | null;
    setSyncing: (v: boolean) => void;
    setProgress: (v: SyncStatusResponse | null) => void;
    setSyncStatus: (v: string | null) => void;
    setSyncFailed: (v: boolean) => void;
    setInitialSyncRequired: (v: boolean) => void;
    setLastSyncedAt: (v: Date | null) => void;
    refreshLastSyncedAt: () => Promise<void>;
    /**
     * Drops every company-scoped value so a switch cannot leave the previous company's sync on screen.
     * `initialSyncRequired` is deliberately untouched: the company-switch call sites set it from the new
     * company's status right after switching, and clearing it here would race that write.
     */
    resetSync: () => void;
}

export const useSyncStore = create<SyncState>((set) => ({
    syncing: false,
    progress: null,
    syncStatus: null,
    syncFailed: false,
    initialSyncRequired: false,
    lastSyncedAt: null,
    setSyncing: (syncing) => set({ syncing }),
    setProgress: (progress) => set({ progress }),
    setSyncStatus: (syncStatus) => set({ syncStatus }),
    setSyncFailed: (syncFailed) => set({ syncFailed }),
    setInitialSyncRequired: (initialSyncRequired) => set({ initialSyncRequired }),
    setLastSyncedAt: (lastSyncedAt) => set({ lastSyncedAt }),
    // Fail-silent: the freshness label is informational, so a failed status call leaves the previous
    // value in place rather than blanking it or surfacing an error over the task list.
    refreshLastSyncedAt: async () => {
        try {
            const status = await syncService.getStatus();
            set({ lastSyncedAt: status.lastSyncedAt });
        } catch {
            // keep the last known value
        }
    },
    resetSync: () => set({ syncing: false, progress: null, syncStatus: null, syncFailed: false, lastSyncedAt: null }),
}));
